import { Form, FormsProps, FormSubmittedData, Input, PopOverFooter, Reset, Submit } from '@components-deprecated'

import { Icon } from '@billy/design-system'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { Contact } from '@features/contacts/types/contact'

import { SpecificState } from '../../../types/reduxSaga-deprecated'
import { getRequest } from '../../../utils'
import { AccountSelector, ContactSelector, TaxRateSelector } from '../../app'
import { Account, AccountNature } from '../../app/accounts/types'
import { Organization } from '../../app/organization/types/organization'
import { createBillAndReconcile } from '../action-creators'
import { ExtendedBankLineMatch } from '../types'
import * as Styled from './styles'

export type CreateBillFormProps = FormsProps & {
  bankLineMatch: ExtendedBankLineMatch
  onClose?: () => void
}

export const CreateBillForm = ({ bankLineMatch, onClose, ...rest }: CreateBillFormProps) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState(true)
  const [preselectedTaxRateId, setPreselectedTaxRateId] = useState('')
  const [preselectedAccountId, setPreselectedAccountId] = useState('')
  const [voucherNo, setVoucherNo] = useState(1)
  const organization = useSelector((state: SpecificState) => state.app.organization) as Organization
  const description = bankLineMatch?.bankLines.map((line) => line.description).join(', ')

  const [defaultValues, setDefaultValues] = useState({ voucherNo, description })

  useEffect(() => {
    getRequest(`/v2/organizations/${organization.id}/nextVoucherNo`)
      .then((res) => setVoucherNo(res.nextVoucherNo))
      .catch(() => {
        /* ignore */
      })
      .finally(() => setIsLoading(false))
  }, [organization.id])

  const handleSubmit = (formData: FormSubmittedData) => {
    const { _account, _contact, description, _taxRate, voucherNo } = formData
    dispatch(
      createBillAndReconcile({
        accountId: _account.id,
        bankLineMatch,
        contactId: _contact.id,
        currencyId: _account.currencyId,
        description,
        organizationId: organization.id,
        taxRateId: _taxRate.id || preselectedTaxRateId,
        voucherNo,
      }),
    )
    if (onClose) {
      setTimeout(() => onClose(), 100)
    }
  }

  useEffect(() => {
    setDefaultValues({ voucherNo, description })
  }, [voucherNo, description])

  const handleSelectContact = (contact: Contact) => {
    const { defaultExpenseAccountId, defaultTaxRateId } = contact
    if (defaultExpenseAccountId) {
      setPreselectedAccountId(defaultExpenseAccountId.toString())
    }

    if (defaultTaxRateId) {
      setPreselectedTaxRateId(defaultTaxRateId.toString())
    }
  }

  const handleSelectAccount = (account: Account) => {
    if (account.id !== preselectedAccountId && account.taxRateId) {
      setPreselectedAccountId(account.id)
      setPreselectedTaxRateId(account.taxRateId)
    }
  }

  if (isLoading) {
    return (
      <Styled.LoadingWrapper>
        <Icon icon="circleCheck" />
      </Styled.LoadingWrapper>
    )
  }

  return (
    <Form defaultValues={defaultValues} onSubmit={handleSubmit} {...rest}>
      <ContactSelector name="contact" isVendor required onItemSelect={handleSelectContact} listWidth="100%" />
      <Input name="voucherNo" placeholder={t('voucher_no')} />
      <Input name="description" placeholder={t('description')} required />
      <AccountSelector
        name="account"
        natures={[AccountNature.Expense, AccountNature.Asset]}
        preselectedId={preselectedAccountId}
        onItemSelect={handleSelectAccount}
        pick="id, currencyId"
        listWidth="100%"
        required
      />
      <TaxRateSelector name="taxRate" preselectedId={preselectedTaxRateId} purchase={true} listWidth="100%" />
      <PopOverFooter>
        <Reset onClick={onClose} size="lg">
          {t('cancel')}
        </Reset>
        <Submit>{t('reconcile')}</Submit>
      </PopOverFooter>
    </Form>
  )
}
